var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { LitElement, html, css } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import { iconButton } from './shared';
import './vitesse-icon';
/*
 * <vitesse-dialog> — THE one reusable modal dialog for the whole app. It wraps the
 * NATIVE <dialog> element via showModal(), so the popup renders in the browser's
 * TOP LAYER — never clipped by a parent's overflow / transform / z-index. (That
 * clipping is exactly why an absolutely-positioned dropdown vanished inside the
 * grid header; a top-layer dialog is the structural fix.)
 *
 * Centered card with a heading + close button, a default slot for body content,
 * and a `footer` slot for actions. Closes on the × button, Escape (native), and
 * backdrop click — each emits `dialog-close`. The caller owns `open` (controlled).
 *
 *   <vitesse-dialog .open=${open} heading=${t('grid.chartSettings')}
 *       @dialog-close=${() => (open = false)}>
 *     …content…
 *     <button slot="footer">Done</button>
 *   </vitesse-dialog>
 */
let VitesseDialog = class VitesseDialog extends LitElement {
    constructor() {
        super(...arguments);
        this.open = false;
        this.heading = '';
        this.closeLabel = 'Close';
        /** Optional CSS max width (e.g. "600px"). Default keeps the 440px card; still
         *  clamped to 92vw so it never overflows a small viewport. */
        this.width = '';
        /**
         * MODAL, unless something outside this app has to appear over it.
         *
         * `showModal()` puts the dialog in the browser's TOP LAYER, which is what keeps
         * it from being clipped — and also what makes it unbeatable by z-index. So an
         * overlay we do not own (the identity provider's org dialog, portaled into the
         * body) renders UNDERNEATH no matter how high it stacks itself.
         *
         * Setting this false re-shows the dialog non-modally: same element, same
         * contents, still open and visible behind — just an ordinary positioned element
         * that a z-indexed overlay can cover. The alternative was closing this dialog,
         * which throws away whatever the person was half-way through writing.
         */
        this.modal = true;
        this.onCancel = (e) => {
            e.preventDefault(); // intercept native Escape so the caller drives `open`
            this.emitClose();
        };
        // Backdrop click lands on the <dialog> element itself (content sits in inner divs).
        this.onClick = (e) => {
            if (e.target === this.dlg)
                this.emitClose();
        };
    }
    updated(changed) {
        if (!this.dlg)
            return;
        // A modality change on an OPEN dialog has to re-show it: the top layer is
        // entered by showModal() and left only by close(), so there is no way to
        // demote one in place.
        if (changed.has('modal') && this.open && this.dlg.open) {
            this.dlg.close();
            if (this.modal)
                this.dlg.showModal();
            else
                this.dlg.show();
            return;
        }
        if (!changed.has('open'))
            return;
        if (this.open && !this.dlg.open) {
            if (this.modal)
                this.dlg.showModal();
            else
                this.dlg.show();
        }
        else if (!this.open && this.dlg.open)
            this.dlg.close();
    }
    emitClose() {
        this.dispatchEvent(new CustomEvent('dialog-close', { bubbles: true, composed: true }));
    }
    static { this.styles = [
        iconButton,
        css `
      dialog {
        margin: auto;
        width: min(var(--vd-w, 440px), 92vw);
        max-height: 86vh;
        padding: 0;
        border: 1px solid var(--border-color);
        border-radius: 14px;
        background: var(--card-bg);
        color: var(--text-primary);
        box-shadow: 0 24px 64px rgba(0, 0, 0, 0.5);
        overflow: hidden;
        /* Closed means GONE: display:none so the box is neither laid out nor
         * hit-testable — an unconditional author display overrides the UA's
         * dialog:not([open]){display:none} and leaves an invisible box that
         * eats clicks on whatever it happens to overlap. */
        display: none;
        flex-direction: column;
        /* The closed state IS the base — so the SAME transition runs in both
         * directions (open: starting-style→open; close: open→base). overlay +
         * display use allow-discrete so the element stays painted while it animates
         * out (graceful instant no-op on browsers without @starting-style). */
        opacity: 0;
        transform: translateY(8px) scale(0.98);
        transition:
          opacity 0.2s ease,
          transform 0.2s ease,
          overlay 0.2s ease allow-discrete,
          display 0.2s ease allow-discrete;
      }
      dialog[open] {
        /* Header + footer stay put; the body scrolls when a form is tall. */
        display: flex;
        opacity: 1;
        transform: none;
      }
      @starting-style {
        dialog[open] {
          opacity: 0;
          transform: translateY(8px) scale(0.98);
        }
      }
      dialog::backdrop {
        /* closed state (base) — fades in on open, out on close, symmetrically */
        background: rgba(0, 0, 0, 0);
        backdrop-filter: blur(0);
        transition:
          background 0.2s ease,
          backdrop-filter 0.2s ease,
          overlay 0.2s ease allow-discrete,
          display 0.2s ease allow-discrete;
      }
      dialog[open]::backdrop {
        background: rgba(0, 0, 0, 0.55);
        backdrop-filter: blur(2px);
      }
      @starting-style {
        dialog[open]::backdrop {
          background: rgba(0, 0, 0, 0);
          backdrop-filter: blur(0);
        }
      }
      header {
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        padding: 16px 18px;
        border-bottom: 1px solid var(--border-color);
      }
      h2 {
        margin: 0;
        font-size: 15px;
        font-weight: 600;
        color: var(--text-primary);
      }
      .body {
        padding: 18px;
        padding-bottom: 24px;
        /* Scroll a tall form within the body; header/footer stay fixed. min-height
           lets the flex item actually shrink so overflow engages. */
        overflow-y: auto;
        flex: 1 1 auto;
        min-height: 0;
      }
      footer {
        display: flex;
        justify-content: flex-end;
        gap: 8px;
        padding: 14px 18px;
        border-top: 1px solid var(--border-color);
      }
      footer.empty {
        display: none;
      }
    `,
    ]; }
    render() {
        return html `
      <dialog style=${this.width ? `--vd-w:${this.width}` : ''} @cancel=${this.onCancel} @click=${this.onClick}>
        <header>
          <!-- The heading property covers the plain-text case; the slot lets a caller
               compose a RICH title (e.g. a mapping with its two ends called out)
               rather than spending a whole row of the body restating it. -->
          <h2><slot name="heading">${this.heading}</slot></h2>
          <button class="icon-btn" title=${this.closeLabel} @click=${this.emitClose}>
            <vitesse-icon name="x" size="18"></vitesse-icon>
          </button>
        </header>
        <div class="body"><slot></slot></div>
        <footer><slot name="footer"></slot></footer>
      </dialog>
    `;
    }
};
__decorate([
    property({ type: Boolean })
], VitesseDialog.prototype, "open", void 0);
__decorate([
    property()
], VitesseDialog.prototype, "heading", void 0);
__decorate([
    property()
], VitesseDialog.prototype, "closeLabel", void 0);
__decorate([
    property()
], VitesseDialog.prototype, "width", void 0);
__decorate([
    property({ type: Boolean })
], VitesseDialog.prototype, "modal", void 0);
__decorate([
    query('dialog')
], VitesseDialog.prototype, "dlg", void 0);
VitesseDialog = __decorate([
    customElement('vitesse-dialog')
], VitesseDialog);
export { VitesseDialog };
//# sourceMappingURL=vitesse-dialog.js.map